import { addDays, format, parseISO } from "date-fns";
import { ParentHistoryEntry } from "../types";

/**
 * The day after the one given.
 *
 * Kept in local time throughout, the same way dayBefore is. Stepping a day
 * and then reading the date back off toISOString would cross midnight into
 * the neighbouring day everywhere east of Greenwich.
 */
const dayAfter = (isoDate: string): string => {
  const date = parseISO(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }
  return format(addDays(date, 1), "yyyy-MM-dd");
};

/**
 * Where a new parent picks up: the day after the last one stops applying,
 * which leaves no gap and cannot clash with what is already there.
 *
 * Undefined when there is nothing to follow. An account with no history has
 * no last parent, and one whose stretch runs on has no day after it - that
 * second case cannot take a new parent at all until the open end is closed,
 * so there is no date worth offering for it either.
 *
 * The latest end is taken across every stretch rather than off the last of
 * them, so the order the history arrives in does not matter.
 */
export const dayAfterLastParentEnds = (
  history: ParentHistoryEntry[]
): string | undefined => {
  const ends = history
    .map((entry) => entry.validTo)
    .filter((end): end is string => end !== null);

  if (!ends.length || ends.length !== history.length) {
    return undefined;
  }

  return dayAfter(ends.reduce((latest, end) => (end > latest ? end : latest)));
};

/** A stretch being proposed, before it is part of the history. */
export interface ParentSpan {
  validFrom: string;
  /** Null runs the stretch on with no end. */
  validTo: string | null;
}

/** Whether the stretch still covers the given day. */
const coversFrom = (span: ParentSpan, day: string) =>
  span.validTo === null || span.validTo >= day;

/**
 * Two stretches share at least one day.
 *
 * Each has to reach the other's start for that to happen, and a stretch
 * with no end reaches everything after it.
 */
const overlaps = (a: ParentSpan, b: ParentSpan) =>
  coversFrom(a, b.validFrom) && coversFrom(b, a.validFrom);

/**
 * Why a stretch cannot have these dates, or nothing if it can.
 *
 * Any date is allowed, past included - the only thing that matters is that
 * no two parents claim the same day. An account is charged under one
 * parent at a time, and rewriting when a parent applied is a legitimate
 * correction to want to make.
 *
 * The others are every stretch except the one being changed, so a stretch
 * never conflicts with itself.
 */
export const parentSpanError = (
  others: ParentHistoryEntry[],
  span: ParentSpan
): string | undefined => {
  if (!span.validFrom) {
    return undefined;
  }
  if (span.validTo !== null && span.validTo < span.validFrom) {
    return `A parent cannot end before ${span.validFrom}, the day it starts.`;
  }

  const clash = others.find((other) => overlaps(other, span));
  if (clash) {
    return clash.validTo === null
      ? `#${clash.parentCustomerNo} is the parent from ${clash.validFrom} onwards.`
      : `#${clash.parentCustomerNo} is the parent from ${clash.validFrom} to ${clash.validTo}.`;
  }

  return undefined;
};

/** Every stretch but the one identified, which is the one being changed. */
export const otherStretches = (
  history: ParentHistoryEntry[],
  validFrom: string
): ParentHistoryEntry[] =>
  history.filter((entry) => entry.validFrom !== validFrom);

/**
 * Why no parent can be added yet, or nothing if one can.
 *
 * A parent is added with a start and no end, so it runs on indefinitely.
 * Any stretch already running on indefinitely would therefore share days
 * with it, whatever start is picked - there is no date that avoids the
 * clash. Ending the open one first is the only way through, so that is
 * what this says rather than leaving the calendar to be tried date by
 * date.
 */
export const openEndedStretchError = (
  history: ParentHistoryEntry[]
): string | undefined => {
  const openEnded = history.find((entry) => entry.validTo === null);

  return openEnded
    ? `#${openEnded.parentCustomerNo} has no end date, so it runs on indefinitely. ` +
        `Give it one before adding a parent, or the two would overlap.`
    : undefined;
};
